import React from 'react';
import { proofPoints, testimonials } from '../data/mock';
import { TrendingUp, ArrowDownRight, ArrowUpRight, Percent } from 'lucide-react';

const iconMap = {
  1: TrendingUp,
  2: Percent,
  3: ArrowUpRight,
  4: ArrowDownRight,
};

const ProofSection = () => {
  return (
    <section id="proof" className="py-24 lg:py-32 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-rose-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-bold text-amber-400 uppercase tracking-wider mb-4 px-4 py-2 bg-amber-500/10 rounded-full border border-amber-500/20">
            The Proof
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            Numbers don't lie.
            <br />
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Neither do we.</span>
          </h2>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
          {proofPoints.map((point) => {
            const IconComponent = iconMap[point.id];
            const isNegative = point.metric.startsWith('-');
            return (
              <div
                key={point.id}
                className="group relative bg-white/5 backdrop-blur-sm rounded-2xl md:rounded-3xl p-5 md:p-8 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2"
              >
                <IconComponent className={`w-6 h-6 md:w-8 md:h-8 mb-3 md:mb-4 ${
                  isNegative ? 'text-emerald-400' : 'text-amber-400'
                }`} />
                <div className={`text-3xl md:text-5xl font-black mb-2 ${
                  isNegative 
                    ? 'bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent' 
                    : 'bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent'
                }`}>
                  {point.metric}
                </div>
                <div className="text-sm md:text-lg font-bold text-white mb-1 md:mb-2">
                  {point.label}
                </div>
                <div className="text-xs md:text-sm text-slate-400 hidden md:block">
                  {point.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl md:rounded-3xl p-6 md:p-8 border border-white/10"
            >
              <div className="text-6xl text-violet-400 mb-4">“</div>
              <p className="text-2xl font-bold text-white mb-6 leading-relaxed">
                {testimonial.quote}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500" />
                <div>
                  <div className="font-bold text-white">{testimonial.author}</div>
                  <div className="text-slate-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trust badges */}
        <div className="mt-16 text-center">
          <p className="text-slate-500 text-sm uppercase tracking-wider mb-8">Trusted by growth-focused teams</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
            {['SaaS', 'E-commerce', 'B2B', 'D2C', 'Startups'].map((badge, index) => (
              <span key={index} className="text-white font-bold text-xl">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProofSection;
